import { api } from "./api";
import type {
  MonitorSource,
  MonitoredCallDetail,
  MonitoredCallsPage,
  MonitoredCallsQuery,
  SourceHealth,
  SourceOverview,
} from "@shared/types";

// Appels à /api/monitoring : sources (client × environnement) et relecture
// des conversations par leur export. Tout passe par le serveur MC.

const BASE = "/api/monitoring";

export interface SourceInput {
  clientName: string;
  clientSlug: string;
  environment: "prod" | "preprod";
  baseUrl: string;
  /** Vide en modification = jeton conservé. */
  token: string;
  phoneNumber: string | null;
  testNumbers: string;
  enabled: boolean;
  position: number;
}

export const listSources = () => api<MonitorSource[]>(`${BASE}/sources`);
export const getOverview = () => api<SourceOverview[]>(`${BASE}/overview`);
export const createSource = (input: SourceInput) =>
  api<MonitorSource>(`${BASE}/sources`, { method: "POST", body: JSON.stringify(input) });
export const updateSource = (id: string, input: Partial<SourceInput>) =>
  api<MonitorSource>(`${BASE}/sources/${id}`, { method: "PATCH", body: JSON.stringify(input) });
export const deleteSource = (id: string) => api<void>(`${BASE}/sources/${id}`, { method: "DELETE" });
export const checkSource = (id: string) => api<SourceHealth>(`${BASE}/sources/${id}/health`);

function qs(query: MonitoredCallsQuery): string {
  const p = new URLSearchParams();
  if (query.since) p.set("since", query.since);
  if (query.before) p.set("before", query.before);
  if (query.limit) p.set("limit", String(query.limit));
  if (query.origin) p.set("origin", query.origin);
  if (query.status?.length) p.set("status", query.status.join(","));
  if (query.outcome?.length) p.set("outcome", query.outcome.join(","));
  if (query.q) p.set("q", query.q);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const listSourceCalls = (id: string, query: MonitoredCallsQuery) =>
  api<MonitoredCallsPage>(`${BASE}/sources/${id}/calls${qs(query)}`);
export const getSourceCall = (id: string, callId: string) =>
  api<MonitoredCallDetail>(`${BASE}/sources/${id}/calls/${encodeURIComponent(callId)}`);

// ---- Mise en forme ----

/** « 2 min 05 », « 45 s ». */
export function formatDuration(sec: number | null | undefined): string {
  if (sec == null) return "—";
  if (sec < 60) return `${sec} s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m} min ${String(s).padStart(2, "0")}`;
}

/** Sept caractères d'un SHA, ou le texte tel quel s'il est déjà court. */
export function shortRevision(sha: string | null | undefined): string {
  if (!sha) return "—";
  return /^[0-9a-f]{12,}$/i.test(sha) ? sha.slice(0, 7) : sha;
}

const RELATIVE = new Intl.RelativeTimeFormat("fr-CA", { numeric: "auto" });

/** « il y a 2 h », « hier », « à l'instant ». */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((t - now) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 45) return "à l'instant";
  if (abs < 3600) return RELATIVE.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RELATIVE.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RELATIVE.format(Math.round(diffSec / 86400), "day");
  return new Date(iso).toLocaleDateString("fr-CA");
}

/** « +1 579 999 8485 » pour un numéro nord-américain, sinon tel quel. */
export function prettyPhone(raw: string | null | undefined): string {
  if (!raw) return "—";
  const m = raw.replace(/[^\d+]/g, "").match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : raw;
}

/** Date et heure courtes dans le fuseau du navigateur : « 1 oct., 14 h 02 ». */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export type Period = "24h" | "7d" | "30d" | "all";

export const PERIOD_LABELS: Record<Period, string> = {
  "24h": "24 heures",
  "7d": "7 jours",
  "30d": "30 jours",
  all: "Tout l'historique",
};

export function sinceFor(period: Period, now = Date.now()): string | undefined {
  const days = period === "24h" ? 1 : period === "7d" ? 7 : period === "30d" ? 30 : null;
  return days ? new Date(now - days * 86400_000).toISOString() : undefined;
}
